// WALLET-P1 §6.2: Operator › Billing › Discounts — the top-up bonus ladder (from · bonus ·
// label); "Add tier" and a row's Edit open a modal, Remove confirms in an AlertDialog.
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PercentIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import {
  bpText,
  dollars,
  microsToInput,
  parseDollars,
  parsePercent,
} from "../../billing/billing-money";
import { type BonusTier, billingKeys } from "../../billing/billing-queries";
import { problemText } from "../../ops-parts";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";

export function Discounts({ api }: { api: Api }) {
  const qc = useQueryClient();
  const tiers = useQuery({
    queryKey: billingKeys.tiers(),
    queryFn: () => api.client.operator.topupBonusTiers.list(),
  });
  const [editing, setEditing] = useState<{ tier: BonusTier | null } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: billingKeys.tiers() });
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="discounts">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Discounts</h2>
        <Button size="sm" onClick={() => setEditing({ tier: null })}>
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          Add tier
        </Button>
      </div>
      {tiers.isPending ? (
        <Loading label="Loading discounts" rows={3} />
      ) : tiers.isError ? (
        <LoadError what="Discounts" error={tiers.error} />
      ) : tiers.data.data.length === 0 ? (
        <EmptyState variant="card" icon={PercentIcon} title="No top-up bonus tiers" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="tiers">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">From</TableHead>
              <TableHead scope="col" className="text-right">
                Bonus
              </TableHead>
              <TableHead scope="col">Label</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.data.data.map((t) => {
              const name = `the ${dollars(t.min_amount_micros, { whole: true })} tier`;
              return (
                <TableRow key={t.id} data-tier={t.min_amount_micros} className="group/row">
                  <TableHead scope="row" className="font-normal tabular-nums">
                    {dollars(t.min_amount_micros, { whole: true })}
                  </TableHead>
                  <TableCell className="text-right tabular-nums">+{bpText(t.bonus_bp)}</TableCell>
                  <TableCell className="text-muted-foreground">{t.label ?? "—"}</TableCell>
                  <TableCell className="w-px text-right">
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${name}`}
                        tip="Edit"
                        onClick={() => setEditing({ tier: t })}
                      />
                      <RemoveTier api={api} tier={t} name={name} onDone={refresh} />
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Dialog open={editing !== null} onOpenChange={(o) => (o ? null : setEditing(null))}>
        <DialogContent data-slot="tier-dialog">
          {editing ? (
            <TierForm
              api={api}
              tier={editing.tier}
              onSaved={() => {
                setEditing(null);
                refresh();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function TierForm({
  api,
  tier,
  onSaved,
}: {
  api: Api;
  tier: BonusTier | null;
  onSaved: () => void;
}) {
  const [from, setFrom] = useState(tier ? microsToInput(tier.min_amount_micros) : "");
  const [bonus, setBonus] = useState(tier ? bpText(tier.bonus_bp).replace("%", "") : "");
  const [label, setLabel] = useState(tier?.label ?? "");
  const ids = { from: useId(), bonus: useId(), label: useId() };
  const min = parseDollars(from);
  const bp = parsePercent(bonus);
  const valid = min !== null && min > 0 && bp !== null && bp <= 10_000;
  const save = useMutation({
    mutationFn: () => {
      const body = {
        min_amount_micros: min ?? 0,
        bonus_bp: bp ?? 0,
        label: label.trim() || null,
      };
      return tier
        ? api.client.operator.topupBonusTiers.update({ params: { tier_id: tier.id }, body })
        : api.client.operator.topupBonusTiers.create({ body });
    },
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) save.mutate();
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{tier ? "Edit tier" : "Add tier"}</DialogTitle>
      </DialogHeader>
      <FieldGroup>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={from !== "" && (min === null || min <= 0)}>
            <FieldLabel htmlFor={ids.from}>From ($)</FieldLabel>
            <Input
              id={ids.from}
              inputMode="decimal"
              required
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field data-invalid={bonus !== "" && (bp === null || bp > 10_000)}>
            <FieldLabel htmlFor={ids.bonus}>Bonus %</FieldLabel>
            <Input
              id={ids.bonus}
              inputMode="decimal"
              required
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
            />
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor={ids.label}>Label</FieldLabel>
          <Input
            id={ids.label}
            maxLength={60}
            value={label}
            placeholder="Growth (optional)"
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
        {save.isError ? (
          <FieldError>{problemText(save.error, "The tier was not saved.")}</FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : tier ? "Save" : "Add tier"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function RemoveTier({
  api,
  tier,
  name,
  onDone,
}: {
  api: Api;
  tier: BonusTier;
  name: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.client.operator.topupBonusTiers.delete({ params: { tier_id: tier.id } }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <RowAction
        icon={Trash2Icon}
        label={`Remove ${name}`}
        tip="Remove"
        onClick={() => setOpen(true)}
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Top-ups from now on no longer get its +{bpText(tier.bonus_bp)} bonus.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <FieldError>{problemText(remove.error, "The tier was not removed.")}</FieldError>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? "Removing…" : "Remove"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
